import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getMealEntries, cancelMealEntry } from '../../services/mealEntryService';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import OptionsMenu from '../../components/ui/OptionsMenu';
import PersonCell from '../../components/ui/PersonCell';

const Container = styled.div``;

const ModalOverlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  width: 440px; max-width: 90vw;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
  display: flex; flex-direction: column; gap: 14px;
`;

const ModalTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 4px;
`;

const ModalMessage = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px; color: #64748b; margin: 0;
`;

const ModalActions = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px; font-weight: 600;
  background: ${({ $variant }) =>
    $variant === 'granted' ? '#2e7d3215'
    : $variant === 'denied' ? '#E5393515'
    : '#94a3b815'};
  color: ${({ $variant }) =>
    $variant === 'granted' ? '#2e7d32'
    : $variant === 'denied' ? '#E53935'
    : '#64748b'};
`;

export default function MealEntriesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 20, pageIndex: 0 });
  const [queryParams, setQueryParams] = useState({ pageSize: 20, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const lastValidParams = useRef(null);

  const [cancelModal, setCancelModal] = useState({ isOpen: false, entry: null, reason: '' });
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidParams.current || { pageNumber: 1, pageSize: 20 };
    lastValidParams.current = params;
    setFetching(true);
    getMealEntries(params)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 20,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTick]);

  const openCancelModal = useCallback((row) => {
    setCancelModal({ isOpen: true, entry: row, reason: '' });
  }, []);

  const closeCancelModal = useCallback(() => {
    setCancelModal({ isOpen: false, entry: null, reason: '' });
  }, []);

  const confirmCancel = useCallback(async () => {
    if (!cancelModal.reason?.trim()) return;
    setCancelling(true);
    try {
      await cancelMealEntry(cancelModal.entry.id, { reason: cancelModal.reason.trim() });
      setRefreshTick((p) => p + 1);
      closeCancelModal();
    } catch { /* interceptor */ }
    setCancelling(false);
  }, [cancelModal, closeCancelModal]);

  const actionOptions = useMemo(() => {
    if (!canWrite('CN')) return [];
    return [{
      type: 'cancel',
      label: t('canteen.cancelEntry'),
      onClick: openCancelModal,
      disabled: (row) => row.cancelled || !row.granted,
    }];
  }, [t, canWrite, openCancelModal]);

  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'entryTime', label: t('canteen.entryTime'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => v ? new Date(v).toLocaleString() : '-',
      },
    },
    { name: 'userFullName', label: t('canteen.user'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.userProfilePicture} />
      ), filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'mealName', label: t('canteen.meal'), options: { filter: false, sort: false } },
    { name: 'accessPointName', label: t('canteen.accessPoint'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    {
      name: 'granted', label: t('common.status') || 'Durum',
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          if (row.cancelled) return <StatusBadge $variant="cancelled">{t('canteen.cancelled')}</StatusBadge>;
          if (row.granted) return <StatusBadge $variant="granted">{t('canteen.granted')}</StatusBadge>;
          return <StatusBadge $variant="denied">{t('canteen.denied')}</StatusBadge>;
        },
      },
    },
    {
      name: 'denyReason', label: t('canteen.denyReason'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v ? t(`responseErrors.${v}`, { defaultValue: v }) : '-',
      },
    },
    {
      name: 'costAtEntry', label: t('canteen.cost'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return v != null ? `${v} ${row?.currencyAtEntry || ''}`.trim() : '-';
        },
      },
    },
  ], [t, data]);

  const handleTableChanged = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => <OptionsMenu actions={actionOptions} row={data[tableMeta.rowIndex]} />,
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <DataTable
        title={t('canteen.entries')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleTableChanged}
      />

      {cancelModal.isOpen && (
        <ModalOverlay onClick={closeCancelModal}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t('canteen.cancelTitle')}</ModalTitle>
            <ModalMessage>{t('canteen.cancelMessage')}</ModalMessage>
            <TextInput
              label={t('canteen.cancelReason')}
              value={cancelModal.reason}
              onChange={(e) => setCancelModal((p) => ({ ...p, reason: e.target.value }))}
              multiline
              required
            />
            <ModalActions>
              <Button variant="ghost" onClick={closeCancelModal} disabled={cancelling}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={confirmCancel}
                disabled={cancelling || !cancelModal.reason?.trim()}
              >
                {cancelling ? '...' : t('canteen.cancelEntry')}
              </Button>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
}
